import styled from "@emotion/styled";
import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  FieldSelect,
  FONT_WEIGHT_SEMI_BOLD,
  Form,
  FormField,
  FormikForm,
  SkiliticsIcon,
  transientOptions,
} from "@skilitics/designer-ui-components";
import { contentLanguagesSelector } from "@skilitics/nautilus-core-designer-data";

import { commitPendingChanges, useEventPublish } from "../events";
import { activeContentLanguageCodeSelector } from "../state";
import { LanguageEventTypes } from "./languageEvents";
import { useSetActiveContentLanguageCode } from "./useActiveContentLanguageCode.ts";

const Container = styled.div`
  width: 100%;

  fieldset {
    border: none;
  }

  &.disabled {
    pointer-events: none;
  }

  .active-language-switcher-select {
    height: 36px;
  }
`;

const ActiveLanguageValue = styled.div`
  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizeSmall};

  .fa-icon-wrapper {
    color: inherit;
    font-size: 16px;
    margin-right: 8px;
  }
`;

const StyledForm = styled(Form)`
  margin: 0;
  padding: 0;
`;

const StyledFieldSelect = styled(FieldSelect, transientOptions)<{ $isPrimaryLanguage: boolean }>`
  color: ${({ theme, $isPrimaryLanguage }) => ($isPrimaryLanguage ? theme.primaryStrong : theme.secondaryStrong)};
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

type FormValues = {
  activeLanguage: string;
};

type Props = {
  disabled?: boolean;
};

export const ActiveLanguagePicker = ({ disabled = false }: Props) => {
  const contentLanguages = useSelector(contentLanguagesSelector);
  const activeLanguageCode = useSelector(activeContentLanguageCodeSelector);
  const setActiveLanguageCode = useSetActiveContentLanguageCode();

  const publish = useEventPublish();

  const handleActiveLanguageSubmit = ({ activeLanguage: selectedLanguage }: FormValues) => {
    commitPendingChanges();
    setActiveLanguageCode(selectedLanguage);
    publish(LanguageEventTypes.ACTIVE_LANGUAGE_CODE_CHANGED, selectedLanguage);
  };

  const options = contentLanguages.map(({ language: { code, title } }) => ({
    label: `${code} - ${title}`,
    value: code,
  }));

  const activeLanguageLabel = useMemo(() => {
    const lang = contentLanguages.find(({ code }) => code === activeLanguageCode);
    return lang ? `${lang.language.title.split("-")[0]} - ${lang.code}` : "";
  }, [activeLanguageCode, contentLanguages]);

  if (contentLanguages.length === 0) {
    return null;
  }

  return (
    // Disabled is specifically on the container, and not the Select, to prevent the Select from
    // flashing grey during network requests - it was too distracting during normal use.
    <Container className={disabled ? "disabled" : ""}>
      <FormikForm<FormValues>
        initialValues={{ activeLanguage: activeLanguageCode }}
        onSubmit={handleActiveLanguageSubmit}
        dataTest="active-language-form"
        submitOnBlur
      >
        {({ values }) => (
          <StyledForm>
            <FormField<FormValues> name="activeLanguage">
              <StyledFieldSelect
                variant="outlined"
                className="active-language-switcher-select"
                options={options}
                $isPrimaryLanguage={values.activeLanguage === contentLanguages[0].language.code}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "center",
                  },
                }}
                renderValue={() => (
                  <ActiveLanguageValue>
                    <SkiliticsIcon icon="ai-text-translate" /> {activeLanguageLabel}
                  </ActiveLanguageValue>
                )}
              />
            </FormField>
          </StyledForm>
        )}
      </FormikForm>
    </Container>
  );
};
